/**
 * Confirmation modal for scheduling conflicts.
 *
 * Promise-based pattern matching the concurrent Zoom call modal in AgendaBuilderPage.
 * Rendered via createPortal to document.body.
 *
 * @module components/common/ScheduleConflictModal
 */

import { createPortal } from 'react-dom'
import { AlertTriangle } from 'lucide-react'
import { format, parseISO, isValid } from 'date-fns'

function formatTime(isoStr) {
  if (!isoStr) return ''
  const d = parseISO(isoStr)
  if (!isValid(d)) return ''
  return format(d, 'EEE, MMM d • h:mm a')
}

/**
 * @param {{
 *   overlapping: Array<{meetingId: string, title: string, scheduledStartTime: string, scheduledEndTime: string}>,
 *   onConfirm: () => void,
 *   onCancel: () => void
 * }} props
 */
export default function ScheduleConflictModal({ overlapping = [], onConfirm, onCancel }) {
  return createPortal(
    <div className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/40">
      <div className="max-w-md w-full mx-4 rounded-2xl bg-white p-6 shadow-xl">
        {/* Icon */}
        <div className="flex items-center justify-center w-12 h-12 rounded-full bg-amber-100 mx-auto mb-4">
          <AlertTriangle className="w-6 h-6 text-amber-600" />
        </div>

        {/* Title */}
        <h3 className="text-lg font-semibold text-gray-900 text-center mb-2">
          Scheduling conflict
        </h3>

        {/* Body */}
        <p className="text-sm text-gray-600 text-center mb-3">
          This meeting overlaps with {overlapping.length} existing meeting{overlapping.length > 1 ? 's' : ''}:
        </p>

        <ul className="space-y-2 mb-6 max-h-48 overflow-y-auto">
          {overlapping.slice(0, 10).map((m, i) => {
            const start = m.scheduledStartTime || m.startTime
            const end = m.scheduledEndTime || m.endTime
            return (
              <li key={m.meetingId || i} className="flex items-start gap-2 p-2 rounded-lg bg-gray-50 text-sm">
                <span className="shrink-0 mt-0.5 text-amber-500">•</span>
                <div className="min-w-0">
                  <p className="font-medium text-gray-900 truncate">{m.title}</p>
                  {start && (
                    <p className="text-xs text-gray-500">
                      {formatTime(start)}{end ? ` – ${formatTime(end)}` : ''}
                    </p>
                  )}
                </div>
              </li>
            )
          })}
        </ul>

        {/* Buttons */}
        <div className="flex gap-3">
          <button
            type="button"
            onClick={onCancel}
            className="flex-1 px-4 py-2.5 rounded-lg border border-gray-300 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
          >
            Go back
          </button>
          <button
            type="button"
            onClick={onConfirm}
            className="flex-1 px-4 py-2.5 rounded-lg bg-amber-500 text-sm font-medium text-white hover:bg-amber-600 transition-colors"
          >
            Schedule anyway
          </button>
        </div>
      </div>
    </div>,
    document.body
  )
}
